import { type ReactNode, useEffect, useRef, useState } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { emailOtp, triggerHaptic } from '@repo/shared/client';
import { errorTracker } from '@repo/shared/common';
import { sessionQueryOptions, sessionQueryKey } from '~/lib/queries/session';
import {
  currentOrganizationQueryKey,
  myOrganizationsQueryKey,
  useCompleteOnboarding,
} from '~/lib/queries/organization';
import { PORTFOLIO_KEY } from '~/features/portfolio/use-portfolio';
import { resolveLandingRoute } from '~/lib/landing';
import { markOnboardingComplete } from '~/lib/stores/onboarding-celebration';
import { CodeInput } from '~/components/auth/CodeInput';
import { useResendCooldown } from '~/lib/hooks/use-resend-cooldown';
import { Stack, Button, Reveal, Body, Lottie } from '~/components/ui';
import { OnboardingHeader } from '~/components/onboarding/OnboardingHeader';
import { ONBOARDING_VERIFY_LOTTIE } from '~/components/onboarding/lottie-assets';
import styles from '~/components/onboarding/onboarding-step.module.css';

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

export const Route = createFileRoute('/_authed/onboarding/verify-email')({
  beforeLoad: async ({ context }) => {
    // Already-verified users have no business on this step — push them to the
    // next onboarding gate (create-organization, which itself forwards on to the
    // dashboard when an org already exists). Resolved server-side so a verified
    // user never flashes the form.
    const session = await context.queryClient.ensureQueryData(sessionQueryOptions());
    if (session?.user.emailVerified) {
      throw redirect({ to: '/onboarding/create-organization' });
    }
  },
  component: VerifyEmailRoute,
});

function VerifyEmailRoute(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // `_authed` gated auth in beforeLoad and returned the session onto the context.
  const { session } = Route.useRouteContext();
  const email = session.user.email;

  const completeOnboarding = useCompleteOnboarding();
  const cooldown = useResendCooldown(RESEND_COOLDOWN_SECONDS);

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const sentOnceRef = useRef(false);

  // Send the verification code exactly once on mount (Strict Mode double-invokes
  // effects in dev, so guard with a ref). The resend button drives subsequent sends.
  useEffect(() => {
    if (sentOnceRef.current) return;
    sentOnceRef.current = true;
    void sendCode();
    // Send once on mount; the resend button drives any subsequent sends. The
    // ref-guard (not the dep array) is what keeps this to a single send.
  }, []);

  async function sendCode(): Promise<void> {
    cooldown.start();
    try {
      const { error: sendError } = await emailOtp.sendVerificationOtp({
        email,
        type: 'email-verification',
      });
      if (sendError) {
        errorTracker.logMessage('verify-email send otp error', {}, 'info');
      }
    } catch (err) {
      const wrapped = err instanceof Error ? err : new Error(String(err));
      errorTracker.captureException(wrapped, { context: 'verify-email send otp' });
    }
  }

  // The completed code is passed IN (from the CodeInput's onComplete or the form
  // submit) rather than read from state — reading `code` from the closure raced the
  // final keystroke's state update, so an auto-submit saw the previous (5-digit)
  // value and flashed a false "incorrect code" error. Taking the value as an
  // argument makes the check see exactly what the user entered.
  async function verifyAsync(submitted: string): Promise<void> {
    triggerHaptic();
    if (submitted.length !== CODE_LENGTH) {
      setError(t('onboarding.verifyEmail.error'));
      return;
    }
    setError('');
    setVerifying(true);
    try {
      const { error: verifyError } = await emailOtp.verifyEmail({ email, otp: submitted });
      if (verifyError) {
        setError(t('onboarding.verifyEmail.error'));
        setVerifying(false);
        return;
      }

      // The email is now verified server-side. REMOVE the cached session (not just
      // invalidate it) so the `_protected`/onboarding gates — which read it via
      // `ensureQueryData`, with no active observer to trigger a refetch — fetch the
      // fresh emailVerified=true value instead of the stale cached one. (Mirrors the
      // sign-in flow's `removeQueries`.)
      queryClient.removeQueries({ queryKey: sessionQueryKey });

      // Accept any pending invite(s) for this email and learn whether the user now
      // belongs to an org. An invited (auto-accepted) user has finished onboarding
      // here — raise the celebration and send them to their home surface; everyone
      // else still needs to create an org.
      const result = await completeOnboarding.mutateAsync({});
      if (result.hasOrg) {
        // The auto-accept just gave this user an org, but the pre-accept
        // `{ organization: null }` read may still sit in the cache — and the
        // mutation's invalidate does NOT refetch it (no active observer). EVICT
        // the org + portfolio keys (exactly like create-organization does) so
        // `resolveLandingRoute` and the `_protected` gate fetch the fresh
        // membership instead of bouncing the invitee to create a duplicate org.
        queryClient.removeQueries({ queryKey: [...currentOrganizationQueryKey] });
        queryClient.removeQueries({ queryKey: [...myOrganizationsQueryKey] });
        queryClient.removeQueries({ queryKey: [...PORTFOLIO_KEY] });
        markOnboardingComplete();
        void navigate({ to: await resolveLandingRoute(queryClient), replace: true });
      } else {
        void navigate({ to: '/onboarding/create-organization', replace: true });
      }
      // Leave `verifying` true on success — the navigation unmounts this form.
    } catch (err) {
      const wrapped = err instanceof Error ? err : new Error(String(err));
      errorTracker.captureException(wrapped, { context: 'verify-email verify' });
      setError(t('onboarding.verifyEmail.error'));
      setVerifying(false);
    }
  }

  function handleResend(): void {
    if (cooldown.isCoolingDown) return;
    triggerHaptic();
    setError('');
    void sendCode();
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void verifyAsync(code);
      }}
      noValidate
      data-testid="verify-email-step"
    >
      <Reveal variant="fade">
        <Stack gap="lg">
          <OnboardingHeader
            mark={
              <div className={styles.lottieMark}>
                <Lottie
                  src={ONBOARDING_VERIFY_LOTTIE}
                  width={104}
                  height={104}
                  loop={false}
                  ariaLabel={t('onboarding.verifyEmail.illustrationAlt')}
                />
              </div>
            }
            title={t('onboarding.verifyEmail.title')}
            subtitle={
              <>
                {t('onboarding.verifyEmail.subtitleLead')}{' '}
                <span className={styles.email}>{email}</span>
              </>
            }
          />

          <Stack gap="sm">
            <CodeInput
              value={code}
              onChange={(value) => {
                setCode(value);
                if (error) setError('');
              }}
              onComplete={(value) => void verifyAsync(value)}
              error={error}
              autoFocus
              disabled={verifying}
            />

            <Button
              type="submit"
              size="lg"
              fullWidth
              loading={verifying}
              disabled={code.length !== CODE_LENGTH}
              data-testid="verify-submit"
            >
              {t('onboarding.verifyEmail.verify')}
            </Button>
          </Stack>

          <div className={styles.resendRow}>
            <Body size="sm" as="span" className={styles.resendPrompt}>
              {t('onboarding.verifyEmail.resendPrompt')}
            </Body>
            <Button
              variant="subtle"
              size="sm"
              type="button"
              onClick={handleResend}
              disabled={cooldown.isCoolingDown}
              data-testid="resend-code"
            >
              {cooldown.isCoolingDown
                ? t('onboarding.verifyEmail.resendIn', { seconds: cooldown.secondsLeft })
                : t('onboarding.verifyEmail.resend')}
            </Button>
          </div>
        </Stack>
      </Reveal>
    </form>
  );
}
